"use client";

import { useCallback, useEffect, useLayoutEffect, useSyncExternalStore } from "react";
import { setThemePreference, syncTheme } from "@/lib/theme";

// useLayoutEffect so the theme is right before the browser paints the page;
// the server has no layout phase, so fall back to useEffect there.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
  return () => observer.disconnect();
}

const readIsDark = () => document.documentElement.classList.contains("dark");
const serverIsDark = () => false;

/**
 * The marketing shells' theme toggle. The icon reads the class on <html>
 * itself, so it can never disagree with what the page is showing, and the
 * theme is re-applied on mount so arriving from a light-only page by
 * client-side navigation restores the visitor's choice.
 */
export function useThemeToggle(): { isDark: boolean; toggle: () => void } {
  const isDark = useSyncExternalStore(subscribe, readIsDark, serverIsDark);

  useIsomorphicLayoutEffect(() => {
    syncTheme();
  }, []);

  const toggle = useCallback(() => setThemePreference(!readIsDark()), []);

  return { isDark, toggle };
}
